import { defineComponent as _defineComponent } from 'vue'

import {
  computed,
  defineComponent,
  onMounted,
  ref,
  ssrRef,
  useAsync,
  useContext,
  useMeta,
  useRoute,
  useRouter,
  useStore,
  watchEffect,
} from "@nuxtjs/composition-api";
import DOMPurify from "isomorphic-dompurify";

import {
  useAmplitude,
  useAuth,
  useBranchService,
  useCartItem,
  useConfig,
  useLinkService,
  useLtk,
  useProduct,
  useProductDetails,
  useProductSet,
  useProductSetProduct,
  useRetailerProductImages,
  useRetailerProductOptions,
  useRetailerProductPolicies,
  useTranslate,
  useValidation,
} from "@/composables";
import { useCartsStore, useCheckoutStore, useRetailerProductLinksStore } from "@/store";
import { getAmplitudeStore } from "@/store/amplitude";
import { getLikesStore } from "@/store/likes";
import { getLtksStore } from "@/store/ltks";
import { getProductDetailsStore } from "@/store/product-details";
import { getProductSetsStore } from "@/store/product-sets";
import { type ProductSetFeed } from "@/store/product-sets/types";
import { getProductsStore } from "@/store/products";
import { getProfilesStore } from "@/store/profiles";
import { getRetailerProductsStore } from "@/store/retailer-products";
import { NOT_FOUND } from "@/utils/enums/http-codes";
import { FLAG_DUAL_ATTRIBUTION } from "@/utils/experiments/constants";
import logger, { logLevel } from "@/utils/logger";
import { toCamel } from "@/utils/transform/text";

const __default__ = defineComponent({
  name: "RetailerProduct",
  head(this: Vue) {
    return this.$nuxt.context.$metaService.setPageMeta({});
  },
  fetchDelay: 0,
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const auth = useAuth();
const amplitude = useAmplitude();
const ctx = useContext();
const config = useConfig();
const { t } = useTranslate();
const linkService = useLinkService();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { requiredRule } = useValidation();
const amplitudeStore = getAmplitudeStore(store);
const checkoutStore = useCheckoutStore();
const likesStore = getLikesStore(store);
const branch = useBranchService();
const ltksStore = getLtksStore(store);
const productDetailsStore = getProductDetailsStore(store);
const productSetsStore = getProductSetsStore(store);
const productsStore = getProductsStore(store);
const retailerProductsStore = getRetailerProductsStore(store);
const profilesStore = getProfilesStore(store);
const cartsStore = useCartsStore();
const retailerProductLinksStore = useRetailerProductLinksStore();

const id = computed(() => route.value.params.id);
const ltkId = computed(() => {
  const li = route.value.query.li;
  return li ? String(li) : "";
});
const productSetId = computed(() => {
  const ci = route.value.query.ci;
  return ci ? String(ci) : "";
});
const rsHash = computed(() => {
  const rshash = route.value.query.rshash;
  return rshash ? String(rshash) : "";
});
const hlc = computed(() => {
  const hlc = route.value.query.hlc;
  return hlc ? String(hlc) : "";
});
const productId = ssrRef("", "productId");
const productIndex = ssrRef<number | null>(null, "productIndex");
const profileId = ssrRef("", "profileId");
const isFormValid = ref(false);
const hasNoOptions = ssrRef(false, "hasNoOptions");
const isAddToCartInProgress = ref(false);
const isUpdateCartInProgress = ref(false);
const isCheckoutInProgress = ref(false);
const selectedOptions = ref<string[]>([]);

const selectedProductVariantId = computed<string | undefined>({
  get() {
    // If the retailer product has no options to choose from, then set the first variant as the selected
    // product variant as there should be only one variant.
    if (hasNoOptions.value) {
      return catalogRetailerProduct.value?.variants?.[0]?.id;
    }
    const variantId = route.value.query.variant;
    return typeof variantId === "string" ? variantId : undefined;
  },
  set(variantId: string | undefined) {
    const query = route.value.query;
    const newQuery = { ...query };
    if (variantId) {
      newQuery.variant = variantId;
    } else {
      delete newQuery.variant;
    }
    if (query.variant !== newQuery.variant) {
      router.replace({ query: newQuery });
    }
  },
});

const filterDefaultOption = (option: RetailerProductOptions): boolean => {
  return !(option.values.length === 1 && option.name === "Title" && option.values[0] === "Default Title");
};

const fetch = async () => {
  if (id.value) {
    await productDetailsStore.fetchProductDetailById(id.value);
  }
  const productDetails = productDetailsStore.productDetails[id.value];
  if (productDetails === undefined) {
    return ctx.error({ statusCode: NOT_FOUND });
  }

  const retailerProductId = productDetails.catalogRetailerProductId;
  if (!productDetails.hasVariants || retailerProductId === undefined) {
    logger(logLevel.ERROR, "Error this product is not supported on the PDP", {
      productDetailId: productDetails.id,
      hasVariants: productDetails.hasVariants,
      catalogRetailerProductId: retailerProductId,
    });
    return ctx.error({ statusCode: NOT_FOUND });
  }

  // Group all additional API requests into a Promise.allSettled so they can run concurrently.
  const promises = [];

  // Fetch the retailer product
  promises.push(retailerProductsStore.fetchRetailerProductById(retailerProductId));

  // If a ltk id query param was provided, fetch the ltk
  if (ltkId.value) {
    promises.push(ltksStore.fetchLtkById({ id: ltkId.value }));
  }

  // If a product set id query param was provided, fetch the product set and products
  if (productSetId.value && productSetsStore.feeds[productSetId.value] === undefined) {
    promises.push(
      productSetsStore.fetchProductSet({ id: productSetId.value }),
      productSetsStore.fetchProductSetProducts({ id: productSetId.value }),
    );
  }

  // If a hlc query param was provided, fetch the hyperlink for off-property
  if (hlc.value) {
    retailerProductLinksStore.fetchRetailerProductLinkById({ id: retailerProductId, hlc: hlc.value });
  }

  // Resolve all API requests and log all errors.
  const results = await Promise.allSettled(promises);
  results.forEach((result) => {
    if (result.status === "rejected") {
      logger(logLevel.ERROR, "Error fetching API request for PDP", {
        productDetailsId: id,
        error: result.reason,
      });
    }
  });

  const catalogRetailerProduct = retailerProductsStore.retailerProducts[retailerProductId];
  if (catalogRetailerProduct === undefined) {
    return ctx.error({ statusCode: NOT_FOUND });
  }

  hasNoOptions.value = [...catalogRetailerProduct.options].filter(filterDefaultOption).length === 0;

  let ltk: Ltk | undefined;
  if (ltkId.value) {
    ltk = ltksStore.ltks[ltkId.value];
    if (ltk === undefined) {
      return ctx.error({ statusCode: NOT_FOUND });
    }
    profileId.value = ltk.profileId;
  }

  let productSetFeed: ProductSetFeed | undefined;
  if (productSetId.value) {
    productSetFeed = productSetsStore.feeds[productSetId.value];
    if (productSetFeed === undefined) {
      return ctx.error({ statusCode: NOT_FOUND });
    }
    profileId.value = productSetsStore.productSets[productSetId.value]?.profileId ?? "";
  }

  // Attempt to find the product id for these product details from the source type (ltk, product set, gift guide).
  if (ltk) {
    const ltkProductIds = ltk?.productIds ?? [];
    for (const [i, ltkProductId] of ltkProductIds.entries()) {
      if (productsStore.products[ltkProductId]?.productDetailsId === id.value) {
        productId.value = ltkProductId;
        productIndex.value = i;
        break;
      }
    }
  } else if (productSetFeed) {
    const shopProductIds = productSetFeed?.ids ?? [];
    for (const [i, shopProductId] of shopProductIds.entries()) {
      if (productSetsStore.productSetProducts[shopProductId]?.productReferenceId === id.value) {
        productId.value = shopProductId;
        productIndex.value = i;
        break;
      }
    }
  }

  await profilesStore.fetchProfileById(profileId.value);

  return true;
};

useAsync(() => fetch(), `products-page:${id.value}`);

const { productDetails, catalogRetailerProductId, catalogRetailerProduct } = useProductDetails(id);
const { imageUrls, sizeChartImageUrl } = useRetailerProductImages(catalogRetailerProductId, ltkId);
const { policies } = useRetailerProductPolicies(catalogRetailerProductId);
const { options } = useRetailerProductOptions(catalogRetailerProductId);
const { ltk } = useLtk(ltkId);
const { productSet } = useProductSet(productSetId);
const { productSetProduct } = useProductSetProduct(productSetId, id);
const { product } = useProduct(productId);
const { cartItem } = useCartItem(selectedProductVariantId);

useMeta(() => {
  if (!catalogRetailerProduct.value) {
    return {};
  }

  const metaService = ctx.$metaService;
  const head = metaService.setPageMeta({
    title: t("{productName} curated on LTK", { productName: catalogRetailerProduct.value.title }),
    customTitle: true,
    description: t(
      "Shop {productName} and other curated products on LTK, the easiest way to shop everything from your favorite creators.",
      { productName: catalogRetailerProduct.value.title },
    ),
  });

  const height = 630;
  const width = 1200;
  const image = `${catalogRetailerProduct.value.imageUrl}?fit=crop&w=${width}&h=${height}`;

  head.meta?.push(...metaService.setOGImg(image, width, height));

  const imageUrl = linkService.getImageLink({
    url: catalogRetailerProduct.value.imageUrl,
    width: "450",
    height: "450",
    useDefaultDpr: false,
  });

  head.link?.push({
    vmid: "preload",
    rel: "preload",
    imagesrcset: `${imageUrl}&dpr=1 1x, ${imageUrl}&dpr=2 2x, ${imageUrl}&dpr=3 3x`,
    as: "image",
  });

  head.__dangerouslyDisableSanitizersByTagID = {
    ...head.__dangerouslyDisableSanitizersByTagID,
    // query param & in the image urls are being converted into HTML entities and making this property invalid
    preload: ["imagesrcset"],
  };

  const description = catalogRetailerProduct.value?.variants?.[0]?.description;
  const sanitizedDescription = description
    ? DOMPurify.sanitize(description, { RETURN_DOM: true }).textContent
    : undefined;

  const jsonld = {
    "@context": "http://schema.org/",
    "@type": "Product",
    name: catalogRetailerProduct.value?.title,
    description: sanitizedDescription,
    image: imageUrls.value,
    brand: {
      "@type": "Brand",
      name: catalogRetailerProduct.value?.brand,
    },
    offers: (catalogRetailerProduct.value?.variants ?? []).map((variant) => ({
      "@type": "Offer",
      priceCurrency: catalogRetailerProduct.value?.currency ?? "",
      price: variant.salePrice ?? variant.price,
      itemCondition: "https://schema.org/NewCondition",
      availability: `https://schema.org/${variant?.availability ? "InStock" : "OutOfStock"}`,
      sku: variant.vendorVariantId,
      seller: {
        "@type": "Organization",
        name: variant.brand,
      },
    })),
    url: new URL(`https://${config.ltkSite}/products/${id.value}`).href,
  };

  head.script = [
    {
      type: "application/ld+json",
      json: JSON.parse(JSON.stringify(jsonld)),
    },
  ];

  return head;
});

const ltkLiked = computed(() => Boolean(product.value?.ltkId && likesStore.likes[product.value.ltkId]));

const baseEventParams = computed(() => {
  const policies = catalogRetailerProduct.value?.policies ?? [];
  const isGiftGuide = Boolean(productSet.value && productSet.value.subtype === "GIFT_GUIDE");

  return {
    "ltk-id": ltkId.value,
    "ltk-product-id": product.value?.id,
    "product-id": product.value?.id ?? productDetails.value?.id,
    "product-set-id": productSetId.value,
    ...(isGiftGuide && { "product-set-subtype": "GIFT_GUIDE" }),
    matching: product.value?.matching,
    "advertiser-name": productDetails.value?.advertiserName ?? "retailer",
    "ltk-liked": ltkLiked.value,
    "product-count": ltk.value?.productIds.length ?? productSet.value?.productCount,
    // Index will not exist if originating from an off-property link.
    ...(productIndex.value !== null && { index: productIndex.value }),
    "ltk-direct-platform": "shopify",
    "product-image-count": imageUrls.value.length,
    "has-shipping-policy": policies.findIndex(({ handle }) => handle === "shipping-policy") !== -1,
    "has-return-policy": policies.findIndex(({ handle }) => handle === "refund-policy") !== -1,
    "has-tos-policy": policies.findIndex(({ handle }) => handle === "terms-of-service") !== -1,
    "has-contact-policy": policies.findIndex(({ handle }) => handle === "contact-information") !== -1,
    "has-privacy-policy": policies.findIndex(({ handle }) => handle === "privacy-policy") !== -1,
    "has-size-chart": Boolean(sizeChartImageUrl.value),
  };
});

onMounted(() => {
  amplitude?.sendEvent("/page/product-details", baseEventParams.value);
});

watchEffect(() => {
  if (!catalogRetailerProduct.value) {
    return;
  }
  const variantId = route.value.query.variant;
  // If the `variant` query parameter changes, then update the form values to be the option
  // values of the selected product variant or reset the form values if no product variant is selected.
  let options: string[] = [];
  if (variantId) {
    const variantsMap = catalogRetailerProduct.value.variantsMap;
    options =
      Object.keys(variantsMap)
        .find((key) => variantsMap[key].variantId === variantId)
        ?.split("__") ?? [];
  }
  selectedOptions.value = options;
});

const selectedProductVariant = computed(() => {
  return catalogRetailerProduct.value?.variants?.find(({ id }) => id === selectedProductVariantId.value);
});

const profile = computed(() => {
  return profilesStore.profiles[profileId.value];
});

const isDeactivated = computed(() => {
  if (!catalogRetailerProduct.value || !productDetails.value?.available) {
    return true;
  }
  return Boolean(
    catalogRetailerProduct.value.variants.length === 0 &&
      Object.keys(catalogRetailerProduct.value.variantsMap).length === 0,
  );
});

const retailerProductLink = computed(() => {
  return retailerProductLinksStore.retailerProductLinks[catalogRetailerProductId.value];
});

const dualAttributeLink = computed(() => {
  const retailerIds = productDetails.value?.retailerIds ?? [];
  if (retailerIds.length < 2) {
    return "";
  }

  if (product.value) {
    const hyperlink = product.value.hyperlink ?? "";
    // If the new hyperlink is available, then use the hyperlink to create a product link.
    if (hyperlink) {
      return linkService.getProductLink({
        url: hyperlink,
        ltkId: ltkId.value,
        priceDropPercent: productDetails.value?.percentChange,
      });
    }
    // Else, fallback to old style product link.
    return product.value.links.ltkWeb ?? "";
  } else if (productSetProduct.value) {
    return linkService.getProductLink({
      url: productSetProduct.value.linkUrl,
      productSetId: productSetId.value,
      priceDropPercent: productDetails.value?.percentChange,
    });
  } else if (retailerProductLink.value) {
    return linkService.getProductLink({
      url: retailerProductLink.value.url,
      priceDropPercent: productDetails.value?.percentChange,
    });
  }
});

const description = computed(() => {
  if (selectedProductVariant.value) {
    return selectedProductVariant.value.description;
  }
  return catalogRetailerProduct.value?.variants?.[0]?.description;
});

const optionPlaceholder = t("Select an option");

const isCheckoutButtonDisabled = computed(() => {
  if (isDeactivated.value || !selectedProductVariant.value || !isFormValid.value) {
    return true;
  }
  return !selectedProductVariant.value.availability;
});

const hasAvailableProductVariants = computed(() => {
  return Boolean(catalogRetailerProduct.value?.variants.some((variant) => variant.availability));
});

const isInStock = computed(() => {
  if (selectedProductVariant.value) {
    return selectedProductVariant.value.availability;
  }
  return hasAvailableProductVariants;
});

const checkoutClickParams = computed(() => {
  return Object.fromEntries(linkService.getSearchParams({ ltkId: ltkId.value }));
});

const contentSourceId = computed(() => {
  return ltkId.value || productSetId.value || undefined;
});

const contentSourceType = computed((): ContentSourceType => {
  if (ltkId.value) {
    return "post";
  }
  if (productSetId.value) {
    return "product_set";
  }
  return "off_property";
});

const isOptionValueDisabled = (optionId: string, value: string) => {
  if (!catalogRetailerProduct.value || !hasAvailableProductVariants.value) {
    return true;
  }

  const { variants, variantsMap } = catalogRetailerProduct.value;
  const partialProductVariantKey = toCamel(`${optionId}_${value}`);

  // Find all product variant ids that include this option value.
  const variantIds = Object.entries(variantsMap)
    .filter(([productVariantKey]) => {
      // Split the key to do an exact match on each partial key instead of a partial match on the entire key.
      const partialProductVariantKeys = productVariantKey.split("__");
      return partialProductVariantKeys.includes(partialProductVariantKey);
    })
    .map(([, { variantId }]) => variantId);

  // Check if all product variants are unavailable
  return variantIds.every((variantId) => !variants.find(({ id }) => id === variantId)?.availability);
};

const mapOptionToItems = (option: RetailerProductOptions) => {
  return option.values.map((value) => ({
    value: toCamel(`${option.id}_${value}`),
    text: value,
    disabled: isOptionValueDisabled(option.id, value),
  }));
};

const onOptionValueChanged = () => {
  const productVariantKey = selectedOptions.value.join("__");
  const productVariantId = catalogRetailerProduct.value?.variantsMap?.[productVariantKey]?.variantId;
  selectedProductVariantId.value = productVariantId;
};

const onClickSizeChart = () => {
  amplitude?.sendEvent("/page/product-details/view-size-chart");
};

const onClickDetailsReadMore = () => {
  amplitude?.sendEvent("/page/product-details/read-more");
};

const onClickDualAttributionLink = () => {
  amplitude?.sendExitEvent("/product-browser", {
    "ltk-id": ltkId.value,
    "ltk-product-id": product.value?.id,
    "product-id": id.value,
    matching: product.value?.matching,
    "product-count": ltk.value?.productIds.length ?? productSet.value?.productCount,
    ...(productIndex.value !== null && { index: productIndex.value }),
    "advertiser-name": productDetails.value?.advertiserName ?? "retailer",
    ltkLiked: ltkLiked.value,
    "branch-journey-creative-name": branch.lastDisplayedBanner,
    url: dualAttributeLink.value,
    "referring-component": amplitude?.getReferringComponentByPage(route.value.name ?? ""),
  });
};

const onUpdateCartQuantity = (quantity: number) => {
  if (!cartItem.value || !auth.user) {
    return;
  }

  isUpdateCartInProgress.value = true;
  if (quantity === 0) {
    cartsStore.deleteCartItem({ ids: [cartItem.value.id] });
  } else {
    cartsStore.updateCartItem({ cartItemId: cartItem.value.id, quantity });
  }
  isUpdateCartInProgress.value = false;
};

const onAddToCart = () => {
  if (!selectedProductVariantId.value) {
    return;
  }
  isAddToCartInProgress.value = true;
  const params: PostCartItemParams = {
    includes: ["catalog_retailer_products", "product_details", "retailer_product_variants", "retailers"],
    retailerId: productDetails.value?.retailerId ?? "",
    retailerProductVariantId: selectedProductVariantId.value,
    quantity: 1,
    creatorAccountId: profile.value?.rsAccountId?.toString(),
    productDetailsId: id.value,
    ...(rsHash.value && {
      clickHash: rsHash.value,
    }),
    contentSourceId: contentSourceId.value,
    contentSourceType: contentSourceType.value,
    contentProductId: productId.value || undefined,
  };
  // If the user is not logged in, then save the add to cart action and redirect to the login/signup page.
  if (!auth.user) {
    sessionStorage.setItem("add-to-cart", JSON.stringify(params));
    cartsStore.loggedOutAddCartItemParams = params;
    const referringComponent = amplitude?.getReferringComponentByPage(route.value.name ?? "");
    if (referringComponent) {
      amplitudeStore.setReferringComponent(referringComponent);
    }
    router.push({ path: "/signup", query: { "shopltk-signup-prompt": "add-to-cart" } });
  }
  // Else, add the cart item
  else {
    cartsStore.addCartItem(params);
  }
  amplitude?.sendEvent("/added-product-to-cart", {
    "advertiser-name": productDetails.value?.advertiserName ?? "retailer",
  });
  isAddToCartInProgress.value = false;
};

const onCheckout = async () => {
  isCheckoutInProgress.value = true;

  if (!selectedProductVariant.value) {
    logger(logLevel.ERROR, "Error attempting to checkout without a selected variant");
    isCheckoutInProgress.value = false;
    cartsStore.showCheckoutError();
    return;
  }

  // Step 1: Create a checkout for the select product variant and quantity.
  await checkoutStore.createCheckout({
    deviceId: amplitude?.getDeviceId(),
    profileId: ltk.value?.profileId ?? productSet.value?.profileId,
    retailerProductVariantId: selectedProductVariant.value.id,
    productDetailId: id.value,
    ...(contentSourceId.value && {
      contentSourceId: contentSourceId.value,
    }),
    contentSourceType: contentSourceType.value,
    ...(rsHash.value && {
      clickHash: rsHash.value,
    }),
    quantity: 1,
    clickParams: checkoutClickParams.value,
  });

  // Step 2: Send analytic event to Amplitude.
  amplitude?.sendExitEvent("/page/product-details/checkout-start", {
    ...baseEventParams.value,
    quantity: "1",
    "variant-id": selectedProductVariant.value?.vendorVariantId,
  });

  // Step 3: Complete the checkout by redirecting the user to the checkout page.
  isCheckoutInProgress.value = false;
  if (checkoutStore.checkout?.checkoutUrl) {
    window.location.assign(checkoutStore.checkout.checkoutUrl);
  }
};

return { __sfc: true,auth, amplitude, ctx, config, t, linkService, store, route, router, requiredRule, amplitudeStore, checkoutStore, likesStore, branch, ltksStore, productDetailsStore, productSetsStore, productsStore, retailerProductsStore, profilesStore, cartsStore, retailerProductLinksStore, id, ltkId, productSetId, rsHash, hlc, productId, productIndex, profileId, isFormValid, hasNoOptions, isAddToCartInProgress, isUpdateCartInProgress, isCheckoutInProgress, selectedOptions, selectedProductVariantId, filterDefaultOption, fetch, productDetails, catalogRetailerProductId, catalogRetailerProduct, imageUrls, sizeChartImageUrl, policies, options, ltk, productSet, productSetProduct, product, cartItem, ltkLiked, baseEventParams, selectedProductVariant, profile, isDeactivated, retailerProductLink, dualAttributeLink, description, optionPlaceholder, isCheckoutButtonDisabled, hasAvailableProductVariants, isInStock, checkoutClickParams, contentSourceId, contentSourceType, isOptionValueDisabled, mapOptionToItems, onOptionValueChanged, onClickSizeChart, onClickDetailsReadMore, onClickDualAttributionLink, onUpdateCartQuantity, onAddToCart, onCheckout, FLAG_DUAL_ATTRIBUTION }
}

})